import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import Chip from './Chip';
import './DatasetMultiSelect.css';
import { isAwaitingReviewOnly } from '../../../utils/datasetReviewState';
import { REVIEW_REQUIRED_LABEL } from '../../../utils/reviewRequiredNotice';

/**
 * Searchable multi-select for datasets: a closed trigger naming the selection,
 * which opens a panel with a search box and a checkbox per dataset. Shared by
 * the automation Query step and the Insights widget wizard so the two pickers
 * cannot drift apart.
 *
 * `selectedIds` is kept in the order the author picked, and `onChange` receives
 * the next array in that order. Ids are compared as strings, but a newly ticked
 * dataset is added with the id exactly as its record carries it, so callers
 * that keep numeric or string ids both get back what they put in.
 */
const DatasetMultiSelect = ({
  datasets,
  selectedIds,
  onChange,
  loading = false,
  label = 'Datasets',
  placeholder = 'Select datasets',
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selected = selectedIds.map(String);

  const handleToggle = (ds) => {
    const id = String(ds.id);
    onChange(selected.includes(id)
      ? selectedIds.filter((x) => String(x) !== id)
      : [...selectedIds, ds.id]);
  };

  const filtered = search
    ? datasets.filter((ds) =>
      (ds.name || '').toLowerCase().includes(search.toLowerCase())
    )
    : datasets;

  // Selected ids with no record in the list, e.g. a dataset deleted since the
  // widget was saved. Each gets its own row so the author can untick it;
  // without one the dead id stays selected and every query keeps sending it.
  const known = new Set(datasets.map((ds) => String(ds.id)));
  const unavailable = loading ? [] : selectedIds.filter((x) => !known.has(String(x)));

  return (
    <div className="report-dataset-dropdown" ref={dropdownRef} role="group" aria-label={label}>
      <button
        type="button"
        className="report-input report-dataset-trigger"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="report-dataset-trigger-text">
          {/* Each selected dataset by name, wearing the same Review required
              chip its row in the list wears (Figma "Report Step 2") — the
              trigger is what the author sees once the panel is closed. */}
          {selected.length > 0
            ? selected.map((id, i) => {
                const ds = datasets.find((d) => String(d.id) === id);
                // The separator ends an item rather than starting the next:
                // accessible-name computation puts a space between element
                // children, so a leading ", " would read "Timesheets , Sites".
                return (
                  <span key={id} className="report-dataset-trigger-item">
                    {ds?.name || id}
                    {ds && isAwaitingReviewOnly(ds) && (
                      <Chip variant="alert" className="report-dataset-review-chip">{REVIEW_REQUIRED_LABEL}</Chip>
                    )}
                    {i < selected.length - 1 && ', '}
                  </span>
                );
              })
            : placeholder}
        </span>
        <Icon name={open ? 'expand_less' : 'expand_more'} className="report-dataset-arrow" />
      </button>
      {open && (
        <div className="report-dataset-panel">
          <input
            type="text"
            className="resui-input resui-input--compact report-dataset-search"
            placeholder="Search datasets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="report-dataset-list">
            {loading && (
              <span className="report-helper">Loading...</span>
            )}
            {unavailable.map((x) => (
              <label key={`unavailable-${x}`} className="resui-checkbox report-dataset-item selected">
                <input
                  type="checkbox"
                  className="resui-checkbox-input"
                  checked
                  onChange={() => onChange(selectedIds.filter((y) => String(y) !== String(x)))}
                />
                <span className="resui-checkbox-mark" />
                <span className="report-dataset-name">{`${x} (unavailable)`}</span>
              </label>
            ))}
            {!loading && filtered.length === 0 && unavailable.length === 0 && (
              <span className="report-helper">
                {search ? 'No match.' : 'No datasets.'}
              </span>
            )}
            {filtered.map((ds) => {
              const id = String(ds.id);
              const checked = selected.includes(id);
              return (
                <label key={id} className={`resui-checkbox report-dataset-item${checked ? ' selected' : ''}`}>
                  <input
                    type="checkbox"
                    className="resui-checkbox-input"
                    checked={checked}
                    onChange={() => handleToggle(ds)}
                  />
                  <span className="resui-checkbox-mark" />
                  <span className="report-dataset-name">{ds.name}</span>
                  {/* Marks the dataset, never disables it — exclusion is
                      per-document, so the dataset stays selectable minus its
                      parked files. */}
                  {isAwaitingReviewOnly(ds) && (
                    <Chip variant="alert" className="report-dataset-review-chip">{REVIEW_REQUIRED_LABEL}</Chip>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatasetMultiSelect;
